import { useTranslation } from 'react-i18next';
import type { AlbumCatalogue, AlbumFiguresMap, AlbumPage, AlbumSpread, AlbumTemplate } from '../../types';
import { templateFits } from '../../utils/albumSpreads';
import { isTripMap } from '../../utils/albumMaps';
import { ArrowLeftRight, Icon, RefreshCw, Trash2 } from '../ui/Icon';
import AlbumTemplatePicker from './AlbumTemplatePicker';
import FiguresChoice from './FiguresChoice';

interface Props {
  page: AlbumPage;
  spread: AlbumSpread;
  /** «Page 4», «Pages 4–5», «Front cover»… */
  label: string;
  catalogue: AlbumCatalogue;
  saving: boolean;
  onTemplate: (template: AlbumTemplate) => void;
  onFiguresMap: (figuresMap: AlbumFiguresMap) => void;
  onFigures: (figures: { figuresRoute?: boolean; figuresOutline?: boolean; mapLegend?: boolean }) => void;
  /** ADR-0052: an inner spread goes whole — both its pages. */
  onDeleteSpread: () => void;
  onSwap: () => void;
  /** ADR-0050: only on a spread the draft made for a day. */
  onRedoDay: () => void;
}

/** The selected page's layout, its figures options, and the actions on its spread. */
export default function AlbumPageSettings({ page, spread, label, catalogue, saving, onTemplate, onFiguresMap, onFigures, onDeleteSpread, onSwap, onRedoDay }: Props) {
  const { t } = useTranslation();
  const twoPages = spread.pages.length === 2;
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {label} · {t('album.template')}
        </h3>
        <div className="flex items-center">
          {twoPages && (
            <button
              onClick={onSwap}
              disabled={saving}
              className="btn-icon w-7 h-7 text-slate-400 hover:text-teal-600"
              title={t('album.spread.swap')}
              aria-label={t('album.spread.swap')}
            >
              <Icon as={ArrowLeftRight} size={14} />
            </button>
          )}
          {spread.kind === 'INNER' && (
            <button
              onClick={onDeleteSpread}
              disabled={saving}
              className="btn-icon w-7 h-7 text-slate-400 hover:text-red-500"
              title={t('album.spread.delete')}
              aria-label={t('album.spread.delete')}
            >
              <Icon as={Trash2} size={14} />
            </button>
          )}
        </div>
      </div>
      <AlbumTemplatePicker catalogue={catalogue} value={page.template} onChange={onTemplate} fits={place => templateFits(spread.kind, place)} onCover={spread.kind === 'COVER'} />
      {spread.dayId && (
        <button onClick={onRedoDay} disabled={saving} className="btn-ghost-sm text-xs gap-1.5 text-slate-500">
          <Icon as={RefreshCw} size={12} />
          {t('album.redoDay.button')}
        </button>
      )}
      {page.template === 'STATS' && (
        <div className="flex items-center justify-between gap-3 pt-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t('album.figures.map')}</span>
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-0.5" role="radiogroup" aria-label={t('album.figures.map')}>
            {(['WORLD', 'COUNTRY'] as const).map(option => (
              <button
                key={option}
                role="radio"
                aria-checked={page.figuresMap === option}
                disabled={saving}
                onClick={() => onFiguresMap(option)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer ${
                  page.figuresMap === option ? 'bg-white shadow-sm text-slate-900 dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500'
                }`}
              >
                {t(`album.figures.${option === 'WORLD' ? 'world' : 'country'}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      {(isTripMap(page.template) || page.template === 'DAY_MAP') && (
        <FiguresChoice
          label={t('album.figures.legend')}
          value={page.mapLegend !== false}
          options={[[true, t('album.figures.outlineShow')], [false, t('album.figures.outlineHide')]]}
          disabled={saving}
          onChange={mapLegend => onFigures({ mapLegend })}
        />
      )}
      {page.template === 'STATS' && page.figuresMap === 'COUNTRY' && (
        <>
          <FiguresChoice
            label={t('album.figures.show')}
            value={page.figuresRoute}
            options={[[true, t('album.figures.route')], [false, t('album.figures.stops')]]}
            disabled={saving}
            onChange={figuresRoute => onFigures({ figuresRoute })}
          />
          <FiguresChoice
            label={t('album.figures.outline')}
            value={page.figuresOutline}
            options={[[true, t('album.figures.outlineShow')], [false, t('album.figures.outlineHide')]]}
            disabled={saving}
            onChange={figuresOutline => onFigures({ figuresOutline })}
          />
        </>
      )}
    </section>
  );
}
